/**
 * el-dialog 拖动 + 八方向拉伸指令
 * - 拖动/拉伸后记忆位置与尺寸，关闭再打开不重置
 * - style 守卫拦截 element-ui 重新应用的 margin-top/width，避免关闭淡出时弹框闪回屏幕中间
 *
 * 使用方式：
 * <el-dialog v-dialog-drag-resize title="标题" :visible.sync="dialogVisible">
 *   内容
 * </el-dialog>
 */

const RESIZE_HANDLES = [
  { dir: 'n', style: { top: '0', left: '0', right: '0', height: '6px', cursor: 'n-resize' } },
  { dir: 's', style: { bottom: '0', left: '0', right: '0', height: '6px', cursor: 's-resize' } },
  { dir: 'e', style: { right: '0', top: '0', bottom: '0', width: '6px', cursor: 'e-resize' } },
  { dir: 'w', style: { left: '0', top: '0', bottom: '0', width: '6px', cursor: 'w-resize' } },
  { dir: 'ne', style: { top: '0', right: '0', width: '10px', height: '10px', cursor: 'ne-resize' } },
  { dir: 'nw', style: { top: '0', left: '0', width: '10px', height: '10px', cursor: 'nw-resize' } },
  { dir: 'se', style: { bottom: '0', right: '0', width: '10px', height: '10px', cursor: 'se-resize' } },
  { dir: 'sw', style: { bottom: '0', left: '0', width: '10px', height: '10px', cursor: 'sw-resize' } }
]

const MIN_WIDTH = 300
const MIN_HEIGHT = 200

// 记忆拖动/拉伸后的位置与尺寸
function saveState (dialog) {
  const rect = dialog.getBoundingClientRect()
  dialog._dragState = {
    left: rect.left + 'px',
    top: rect.top + 'px',
    width: dialog.style.width || (rect.width + 'px'),
    height: dialog.style.height || ''
  }
}

// 恢复上次记忆的位置与尺寸
function applyState (dialog, s) {
  dialog.style.position = 'fixed'
  dialog.style.margin = '0'
  dialog.style.left = s.left
  dialog.style.top = s.top
  if (s.width) dialog.style.width = s.width
  if (s.height) dialog.style.height = s.height
}

// style 守卫：element-ui 关闭/打开时会重新应用 margin-top/width，此处检测到偏离即恢复，避免淡出闪动
function setupStyleGuard (dialog) {
  if (dialog._styleGuard) return
  dialog._styleGuard = true
  dialog._styleGuardObserver = new MutationObserver(() => {
    // 拖动/拉伸过程中不干预
    if (dialog._dragging) return
    if (!dialog._dragState) return
    const s = dialog._dragState
    // margin-top 被重置为非 0，或 width 偏离记忆值，则恢复
    if ((dialog.style.marginTop && dialog.style.marginTop !== '0px') || dialog.style.width !== s.width) {
      applyState(dialog, s)
    }
  })
  dialog._styleGuardObserver.observe(dialog, { attributes: true, attributeFilter: ['style'] })
}

function initDragResize (wrapperEl) {
  const dialog = wrapperEl.querySelector('.el-dialog')
  const header = wrapperEl.querySelector('.el-dialog__header')
  if (!dialog || !header) return

  // 每次打开：先恢复上次拖动/拉伸后的位置与尺寸（事件绑定只做一次）
  if (dialog._dragState) {
    applyState(dialog, dialog._dragState)
  }

  if (dialog._dragResizeInit) return
  dialog._dragResizeInit = true

  // 启用 style 守卫（只绑定一次）
  setupStyleGuard(dialog)

  dialog.style.display = 'flex'
  dialog.style.flexDirection = 'column'
  const body = dialog.querySelector('.el-dialog__body')
  if (body) {
    body.style.flex = '1'
    body.style.overflowY = 'auto'
    body.style.minHeight = '0'
  }

  // 拖动
  header.style.cursor = 'move'
  header.addEventListener('mousedown', (e) => {
    if (e.target.closest('.el-dialog__headerbtn')) return
    const rect = dialog.getBoundingClientRect()
    const startX = e.clientX
    const startY = e.clientY
    dialog.style.position = 'fixed'
    dialog.style.margin = '0'
    dialog.style.left = rect.left + 'px'
    dialog.style.top = rect.top + 'px'
    dialog._dragging = true

    function onMouseMove (e) {
      dialog.style.left = (rect.left + e.clientX - startX) + 'px'
      dialog.style.top = (rect.top + e.clientY - startY) + 'px'
    }
    function onMouseUp () {
      dialog._dragging = false
      saveState(dialog)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    e.preventDefault()
  })

  // 拉伸
  RESIZE_HANDLES.forEach(({ dir, style }) => {
    const handle = document.createElement('div')
    handle.className = 'el-dialog__resize-handle'
    Object.assign(handle.style, {
      position: 'absolute',
      zIndex: '10',
      background: 'transparent',
      ...style
    })

    handle.addEventListener('mousedown', (e) => {
      e.stopPropagation()
      e.preventDefault()
      const rect = dialog.getBoundingClientRect()
      const startX = e.clientX
      const startY = e.clientY
      dialog.style.position = 'fixed'
      dialog.style.margin = '0'
      dialog.style.left = rect.left + 'px'
      dialog.style.top = rect.top + 'px'
      dialog._dragging = true

      function onResizeMove (e) {
        const dx = e.clientX - startX
        const dy = e.clientY - startY
        if (dir.includes('e')) { dialog.style.width = Math.max(MIN_WIDTH, rect.width + dx) + 'px' }
        if (dir.includes('w')) {
          const newW = Math.max(MIN_WIDTH, rect.width - dx)
          dialog.style.width = newW + 'px'
          dialog.style.left = (rect.left + rect.width - newW) + 'px'
        }
        if (dir.includes('s')) { dialog.style.height = Math.max(MIN_HEIGHT, rect.height + dy) + 'px' }
        if (dir.includes('n')) {
          const newH = Math.max(MIN_HEIGHT, rect.height - dy)
          dialog.style.height = newH + 'px'
          dialog.style.top = (rect.top + rect.height - newH) + 'px'
        }
        if (dir.includes('s') || dir.includes('n')) {
          const b = dialog.querySelector('.el-dialog__body')
          if (b && dialog.style.height) { b.style.maxHeight = 'none' }
        }
      }
      function onResizeUp () {
        dialog._dragging = false
        saveState(dialog)
        document.removeEventListener('mousemove', onResizeMove)
        document.removeEventListener('mouseup', onResizeUp)
      }
      document.addEventListener('mousemove', onResizeMove)
      document.addEventListener('mouseup', onResizeUp)
    })
    dialog.appendChild(handle)
  })
}

function setupObserver (el) {
  if (el._dragResizeObserver) return
  const observer = new MutationObserver(() => {
    if (el.style.display !== 'none') {
      setTimeout(() => initDragResize(el), 0)
    }
  })
  observer.observe(el, { attributes: true, attributeFilter: ['style'] })
  el._dragResizeObserver = observer
}

export default {
  inserted (el) {
    setupObserver(el)
    if (el.style.display !== 'none') {
      setTimeout(() => initDragResize(el), 0)
    }
  },
  unbind (el) {
    if (el._dragResizeObserver) {
      el._dragResizeObserver.disconnect()
      el._dragResizeObserver = null
    }
    const dialog = el.querySelector('.el-dialog')
    if (dialog && dialog._styleGuardObserver) {
      dialog._styleGuardObserver.disconnect()
      dialog._styleGuardObserver = null
    }
  }
}
